import React from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuCircleHelp, LuCircleX, LuClock } from 'react-icons/lu';
import { ANNUAL_LEAVE_KEY } from '../../../utils/leaveBalance';
import useLeaveText from './leaveText';

const STATUS = {
  PENDING: { icon: LuClock, tone: 'tw-bg-amber-400/20 tw-text-amber-800 tw-ring-amber-500/25 dark:tw-text-amber-200', key: 'pending' },
  APPROVED: { icon: LuCircleCheck, tone: 'tw-bg-emerald-500/15 tw-text-emerald-800 tw-ring-emerald-500/25 dark:tw-text-emerald-200', key: 'approved' },
  REJECTED: { icon: LuCircleX, tone: 'tw-bg-rose-500/15 tw-text-rose-800 tw-ring-rose-500/25 dark:tw-text-rose-200', key: 'rejected' },
};

/** Beklemede / Onaylandı / Reddedildi pill. */
export const StatusBadge = ({ status }) => {
  const { label } = useLeaveText();
  const meta = STATUS[status] || { icon: LuCircleHelp, tone: 'tw-bg-slate-500/10 tw-text-slate-600 tw-ring-slate-500/20 dark:tw-text-slate-300', key: 'unknown' };
  const Icon = meta.icon;
  return (
    <span className={`tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-ring-1 tw-ring-inset ${meta.tone}`}>
      <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {label(`status.${meta.key}`)}
    </span>
  );
};

StatusBadge.propTypes = { status: PropTypes.string };

/** Leave type pill; annual leave (the one counted against the balance) is highlighted. */
export const TypePill = ({ typeKey }) => {
  const { typeLabel } = useLeaveText();
  const annual = typeKey === ANNUAL_LEAVE_KEY;
  return (
    <span className={`tw-inline-flex tw-max-w-full tw-items-center tw-rounded-lg tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold ${annual ? 'tw-bg-teal-500/10 tw-text-teal-800 dark:tw-text-teal-200' : 'tw-bg-violet-500/10 tw-text-violet-800 dark:tw-text-violet-200'}`}>
      <span className="tw-truncate">{typeLabel(typeKey)}</span>
    </span>
  );
};

TypePill.propTypes = { typeKey: PropTypes.string };

export const initialsOf = (name) => {
  const parts = String(name || '-').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''}`.toLocaleUpperCase('tr-TR');
};

/** Round initials avatar. */
export const Initials = ({ name, size = 'md' }) => (
  <span className={`tw-grid tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-font-bold tw-text-white ${size === 'lg' ? 'tw-h-12 tw-w-12 tw-text-base' : 'tw-h-9 tw-w-9 tw-text-xs'}`}>
    {initialsOf(name)}
  </span>
);

Initials.propTypes = { name: PropTypes.string, size: PropTypes.oneOf(['md', 'lg']) };

/** Day count chip ("3 gün"). */
export const DaysChip = ({ days: value }) => {
  const { days } = useLeaveText();
  return (
    <span className="tw-inline-flex tw-whitespace-nowrap tw-rounded-lg tw-bg-slate-500/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-tabular-nums tw-text-slate-700 dark:tw-bg-white/[0.06] dark:tw-text-slate-200">
      {days(value)}
    </span>
  );
};

DaysChip.propTypes = { days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };
